import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { Icon } from '../../../../components/common/icon/Icon';
import { ownTractorPageInfo, sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useRedirectToStoredPage } from '../../../../hooks/page/useRedirectToStoredPage';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../../hooks/store/useStore';
import { userOwnTractorSurveySlice } from '../../../../store/userOwnTractorSurveySlice';
import { browserStorage } from '../../../../utils/browserStorage.util';
import { Button } from '../../../../components/common/buttons/Button';

export const JoinCompletePage = () => {
  const navigate = useNavigate();

  const { navigateToStoredPage } = useRedirectToStoredPage();

  const dispatch = useAppDispatch();
  const userOwnTractorActions = userOwnTractorSurveySlice.actions;

  const storedPagePath = browserStorage.getItem({ storageType: 'sessionStorage', itemKey: 'storedPageForRedirection' });

  const isUserCompleteOwnTractor = storedPagePath === ownTractorPageInfo.marketPricePage.path;
  const isUserCompleteSellTractor = storedPagePath === sellTractorPageInfo.requireImagePage.path;

  const description = isUserCompleteOwnTractor
    ? '하단의 버튼을 눌러\n이전 페이지로 돌아갑니다.'
    : isUserCompleteSellTractor
      ? '작성중이던 페이지로\n돌아가서 작성을 완료 해주세요!'
      : '잠시만요! 🖐🏻\n회원님께서 현재 보유 하고 계신\n트랙터에 대해서 알려주세요!';

  useLayout({
    fixedHeader: <></>,
    fixedHeaderHeight: 0,
    fixedFooter: (
      <ButtonContainer>
        {!(isUserCompleteOwnTractor || isUserCompleteSellTractor) && (
          <Button
            borderType={'square'}
            color={'filledPrimaryStrong'}
            size={'md'}
            text={{ size: 'md', content: '시작하기' }}
            onClick={() => {
              ampli.서베이시작버튼클릭({ '서베이 이름': '회원 보유 트랙터' });
              ReactGA.event('pvm_click_enroll_own_tractor_button', {
                isModal: false,
              });
              dispatch(userOwnTractorActions.changeIsFromJoinCompletePage(true));
              // TODO: 가설 검증 후 엔트리 페이지 삭제하거나, 다시 엔트리 페이지로 이동시켜야 함. https://gint-team.atlassian.net/browse/PVM-3120
              // navigate(ownTractorPageInfo.entryPage.path, { replace: true });
              navigate(ownTractorPageInfo.overviewPage.path, { replace: true });
            }}
          />
        )}
        <Button
          borderType={'square'}
          color={isUserCompleteOwnTractor || isUserCompleteSellTractor ? 'filledPrimaryStrong' : 'filledGreyLight'}
          size={'md'}
          text={{ size: 'md', content: '이전 페이지로 돌아가기' }}
          onClick={navigateToStoredPage}
        />
      </ButtonContainer>
    ),
    fixedFooterHeight: 120,
    shouldShowFooter: false,
  });

  useEffect(() => {
    sessionStorage.removeItem('join-complete');
  }, []);

  return (
    <Container>
      <MainSection>
        <Icon name={'Celebration2'} width={150} height={150} />

        <TextContainer>
          <BigText>{'회원 가입이 완료 되었습니다!'}</BigText>
          <SmallText>{description}</SmallText>
        </TextContainer>

        {!(isUserCompleteOwnTractor || isUserCompleteSellTractor) && (
          <GreyChip>
            <GreyChipText>{'예상 참여 시간'}</GreyChipText>
            <GreenChip>
              <GreenChipText>{'2분 내외'}</GreenChipText>
            </GreenChip>
          </GreyChip>
        )}
      </MainSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100vh - 120px);

  padding: 24px 24px 32px 24px;

  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 32px;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex: 1 0 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
`;

const BigText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
`;

const SmallText = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Body/Large/l-light */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 150%; /* 24px */
`;

const ButtonContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background-color: white;

  padding: 0px 32px 32px 32px;

  gap: 8px;
`;

const GreyChip = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-medium, 8px) var(--radius-large, 16px);
  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const GreyChipText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const GreenChip = styled.div`
  display: flex;
  align-items: center;

  padding: 6px 8px;

  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Color-primary-green, #34c77e);
`;

const GreenChipText = styled.div`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;
